/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import {ErrorHandler, inject, Injectable} from '@angular/core';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { TenantService } from '@src/app/shared/tenant.service';
import { MyInjector } from '@src/app/app.module';
import { HttpClient } from '@angular/common/http';
import { OurAppTrackerService } from '@src/app/shared/our-app-tracker-service';

@Injectable({
    providedIn: 'root'
})
export class OurErrorHandlerService implements ErrorHandler {

    msgSvc = inject(MessageService);
    currentUserSvc = inject(CurrentUserService);
    ourAppTracker = inject(OurAppTrackerService); // Having this injected here, creates it and starts logging
    private readonly errorCooldownMs = 30_000;
    private readonly errorLog = new Map<string, { lastSent: number; suppressed: number }>();

    private shouldReport(message: string): { report: boolean; suppressed: number } {
        const key = `${window.location.pathname.split(';')[0]}|${message}`;
        const entry = this.errorLog.get(key);
        const now = Date.now();
        if (entry && now - entry.lastSent < this.errorCooldownMs) {
            entry.suppressed++;
            return { report: false, suppressed: 0 };
        }
        const suppressed = entry?.suppressed ?? 0;
        this.errorLog.set(key, { lastSent: now, suppressed: 0 });
        return { report: true, suppressed };
    }

    handleError(error) {
        this.logError(error);
        throw error;
    }

    logError(error) {
        let msg1;
        if (typeof error === 'string' || error instanceof String) {
            msg1 = error;
        } else {
            msg1 = error.message ? error.message : ( error.error.message ? error.error.message : error.toString())
        }

        // Happens if you click on screen with chart and navigate away before it was instantiated
        if (!this.recognisedError(error)) {
            const msg = $localize`Error: ${msg1} occured.
                Probably best if you reload your page before continuing. If the problem persists please contact OurOMC`;
            console.warn('Error happened, I caught it...', { error, msg });
            if (window.location.hostname === 'localhost') {
                return;
            }

            // Cannot see the failed http request due to subsequent errors
            //this.msgSvc.show(new MsgDef(msg, 'fail'));
            const errorMsg = `<p>Date: ${new Date()}</p>
                <p>Host: ${window.location.hostname}</p>
                <p>Path: ${window.location.pathname}</p>
                <p>User: ${this.currentUserSvc.currentUser?.name}</p>
                <p>Team: ${this.currentUserSvc.currentTeam?.name}</p>
                <p>Error Message : ${msg1}</p>
            `
            const stack = error.stack ? error.stack : (error.url ? "Calling " + error.url : '');

            const { report, suppressed } = this.shouldReport(msg1);
            if (!this.ourAppTracker.isReloading() && report) {
                localStorage.setItem('lastError', errorMsg);
                console.warn(stack);
                console.warn(this.ourAppTracker.printEvents());

                const url = TenantService.bareAjaxPath + 'logError';
                const data = {
                    teamId: this.currentUserSvc.currentTeam?.id,
                    userId: this.currentUserSvc.currentUser?.id,
                    userName: this.currentUserSvc.currentUser?.name,
                    pathName: window.location.pathname.split(';')[0],
                    fullPath: window.location.href,
                    message: suppressed > 0 ? `[+${suppressed} suppressed] ${msg1}` : msg1,
                    stackTrace: stack + "\n" + this.ourAppTracker.printEvents(),
                }
                const http = MyInjector.instance.get(HttpClient);
                http.post(url, data).subscribe(() => {
                    console.log('wow!');
                }, () => {
                    console.log('surprise!')
                })
            }
        }
    }

    recognisedError(error) {
        if (error?.message === "Highcharts error #13: www.highcharts.com/errors/13/") {
            console.log('Highcharts generates errors if user navigates away from the page, before the chart finished initializing');
            return true;
        }
        if (error?.status === 401) {
            console.log('Unauthenticated error, usually timedout connection or logged in on another device');
            return true;
        }
        if (error?.status === 419) {
            if (this.shouldReport('419').report) {
                this.msgSvc.show(new MsgDef($localize`
Your session timed out. Please refresh the page to continue.
- Desktop: Press F5 or the ↻ icon.
- Mobile: Swipe down or select Reload from your browser menu`, 'warn'));
            }
            return true;
        }
        if (error?.code === 4002) {
            console.log('Navigation error on first route ignored, typically user just typed URL incorrectly');
            return true;
        }
    }

    sendMessage(errorShort : string, errorMsg: string, stack: string) {
        const url = TenantService.bareAjaxPath + 'informus';
        stack += this.ourAppTracker.printEvents();
        const data = {
            message: errorMsg + stack,
            subject: `Whoops - ${errorShort}`,
            sendTo: `support`
        }
        const http = MyInjector.instance.get(HttpClient);
        http.post(url, data).subscribe(() => {
            console.log('wow!');
        }, () => {
            console.log('surprise!')
        })
    }
}
